import React, { FC, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DropdownWithFilter from '../elements/dropdown-with-filter/dropdown-with-filter';
import { VatCodeDto } from '../../models/vat-code.dto';
import { useGetVatCodesQuery } from '../../store/vat-codes/vat-codes.api';
import { BankAccountDto } from '../../models/bank-account.dto';
import { useGetBankAccountsQuery } from '../../store/bank-accounts/bank-accounts.api';
import { AccountingNumberDto } from '../../models/accounting-number.dto';
import { useGetAccountingNumbersQuery } from '../../store/accounting-numbers/accounting-numbers.api';
import { isBlank, isEmpty, notBlank, removeWhiteSpaces } from '../../utils/base';
import AddInvoiceManuallyCompany from './add-invoice-manually-company';
import {
   CreateNonQRInvoice,
   useCreateNonQrInvoiceManuallyMutation
} from '../../store/invoices/invoices.api';
import UploadReceipt from './upload-receipt/upload-receipt';
import { currencies } from '../../utils/currencies';
import { isBicRequired, isValidBic } from '../../utils/bic';
import { useAlert } from '../../provider/alert/userAlert';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PaymentDateButton } from '../invoices-list/action-dropdown/payment-date-field';

interface AddInvoiceManuallyProps {
   open: boolean;
   onClose: () => void;
}

const initialFormData = {
   bankAccount: undefined,
   amount: undefined,
   currency: '',
   companyAccount: '',
   companyBic: '',
   company: {name: '', street: '', buildingNumber: '', postalCode: '', city: '', country: ''},
   reference: '',
   paymentDate: new Date().toISOString().split('T')[0],
   vatCode: '',
   accountingNumber: {accountingNumber: '', name: ''},
   description: '',
}

const AddInvoiceManually: FC<AddInvoiceManuallyProps> = ({ open, onClose }) => {
   const { t } = useTranslation();
   const { setAlert } = useAlert();
   const { data: vatCodes } = useGetVatCodesQuery();
   const { data: bankAccounts } = useGetBankAccountsQuery();
   const { data: accountingNumbers } = useGetAccountingNumbersQuery();
   const [createNonQrInvoiceManually] = useCreateNonQrInvoiceManuallyMutation();

   const [formData, setFormData] = useState<CreateNonQRInvoice>(initialFormData);
   const [file, setFile] = useState<File | undefined>(undefined);
   const [formVersion, setFormVersion] = useState(0);

   const [validated, setValidated] = useState(false);
   const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
   const [amountInput, setAmountInput] = useState<string>('');

   const parseAmount = (raw: string): number | undefined => {
      if (raw.trim() === '') return undefined;
      const normalized = raw.replace(',', '.');
      const n = Number(normalized);
      return Number.isFinite(n) ? n : undefined;
   };

   const handleSave = () => {
      setValidated(true);

      const bicRequired = isBicRequired(formData.companyAccount);
      const bic = formData.companyBic?.trim() ?? '';
      const parsedAmount = parseAmount(amountInput);

      if (!formData.paymentDate ||
         !formData.bankAccount ||
         !formData.amount ||
         !formData.currency.trim() ||
         isBlank(formData.company.name) ||
         isBlank(formData.company.street) ||
         (bicRequired && !isValidBic(bic))
      ) {
         setErrorMsg(t('common:common.required') ?? '');
         return;
      }

      const saveData: CreateNonQRInvoice = {
         ...formData,
         amount: parsedAmount,
         companyAccount: removeWhiteSpaces(formData.companyAccount),
         paymentDate: new Date(formData.paymentDate).toISOString()
      }

      createNonQrInvoiceManually({ data: saveData, file })
         .unwrap()
         .then(() => {
            setFormData(initialFormData);
            setAmountInput('');
            setFile(undefined);
            setFormVersion(v => v + 1);
            setErrorMsg(undefined);
            setValidated(false);
            setAlert('success', t('common:settings.filesUploaded') ?? '');
            onClose();
         })
         .catch(() => {
            setAlert('error', t('common:settings.filesUploadFailed') ?? '');
         });
   };

   const handleOnClose = () => {
      setFormData(initialFormData);
      setAmountInput('');
      onClose();
   }

   const handleVatChange = (e: any) => {
      setFormData({
         ...formData,
         vatCode: e.code
      })
   };

   const handleBankAccountSelection = (account: BankAccountDto) => {
      if (!account?.bankAccountId) return;

      setFormData({
         ...formData,
         bankAccount: account
      })
   };

   const getBankAccountSelectedValue = () => (formData.bankAccount
      ? `${formData.bankAccount.name} (${formData.bankAccount.currency})`
      : '');

   const handleAccountNumberSelection = (accountNumber: AccountingNumberDto) => {
      setFormData({
         ...formData,
         accountingNumber: accountNumber
      })
   };

   const handleCurrencySelection = (currency: string) => {
      setFormData({
         ...formData,
         currency: currency
      });
   };


   const handleChangeAmount = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
   ) => {
      const raw = e.target.value;

      // allow only digits, optional single decimal sep (comma or dot), and up to 2 decimals (adjust if needed)
      const DECIMAL_RE = /^\d*(?:[.,]\d{0,2})?$/;
      if (raw === '' || DECIMAL_RE.test(raw)) {
         setAmountInput(raw);
         const parsed = parseAmount(raw);
         setFormData({
            ...formData,
            amount: parsed, // stays undefined while user types "50." or "50,"
         });
         if (validated) setErrorMsg(undefined);
      }
   };

   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({
         ...formData,
         [e.target.name]: e.target.value,
      });
      if (validated) setErrorMsg(undefined);
   };

   const parsePaymentDate = (value: string) => {
      const [year, month, day] = value.split('-').map(Number);
      return year && month && day ? new Date(year, month - 1, day) : null;
   };

   const handlePaymentDateChange = (date: Date | null) => {
      if (!date) return;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setFormData({
         ...formData,
         paymentDate: `${year}-${month}-${day}`,
      });
      if (validated) setErrorMsg(undefined);
   };

   const isInvalid = (type: string) => {
      if (!validated) return false;

      if (type === 'bank-acc') return !formData.bankAccount;
      if (type === 'amount') return !formData.amount;
      if (type === 'currency') return !formData.currency.trim();
      if (type === 'company-acc') return isBlank(formData.companyAccount);
      if (type === 'company') return isBlank(formData.company.name);
      if (type === 'date') return !formData.paymentDate;
      if (type === 'address') return isBlank(formData.company.street);

      if (type === 'bic') {
         const bicRequired = isBicRequired(formData.companyAccount);
         const bic = formData.companyBic?.trim() ?? '';
         if (bicRequired) return !isValidBic(bic);
         return !!bic && !isValidBic(bic);
      }

      return true;
   }

   const getErrorComponent = (type: string) => {
      if (!isInvalid(type)) return <></>;

      const getDisplayStyle = ({
         display: (type === 'bank-acc' || type === 'currency' || type === 'date') ? 'block' : ''
      });

      const isBicInvalid = type === 'bic' && notBlank(formData.companyBic);

      return (
         <Form.Control.Feedback type="invalid" style={getDisplayStyle}>
            {isBicInvalid ? t('invoice:invoice.invalidBic') : errorMsg}
         </Form.Control.Feedback>
      );
   }

   return (
      <Modal show={open} onHide={onClose} centered dialogClassName='add-invoice-manually-dialog'>
         <Modal.Header closeButton>
            <Modal.Title>{t('common:settings.addInvoiceManually')}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
            <Form key={formVersion}>
               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.bankAccount')}</Form.Label>
                  <DropdownWithFilter
                     className={`toggle ${formData.bankAccount ? '' : 'empty-value'}`}
                     items={bankAccounts || []}
                     selectedValue={getBankAccountSelectedValue()}
                     getMenuItemValue={(bankAccount: BankAccountDto) =>
                        `${bankAccount['name']} (${bankAccount['currency']})`
                     }
                     handleChange={handleBankAccountSelection}
                     handleFilter={(bankAccounts, filterValue) =>
                        bankAccounts.filter((account) =>
                           account.name.toLowerCase().includes(filterValue.toLowerCase()),
                        )
                     }
                  />
                  {getErrorComponent('bank-acc')}
               </Form.Group>

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.amount')}</Form.Label>
                  <Form.Control
                     type="text"
                     name="decimal"
                     pattern="^\d*(?:[.,]\d{0,2})?$"
                     placeholder={t('common:common.enterAmount') ?? ''}
                     value={amountInput}
                     onChange={handleChangeAmount}
                     isInvalid={isInvalid('amount')}
                  />
                  {getErrorComponent('amount')}
               </Form.Group>

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.currency')}</Form.Label>
                  <DropdownWithFilter
                     className={`toggle ${formData.currency ? '' : 'empty-value'}`}
                     items={currencies || []}
                     selectedValue={formData.currency}
                     getMenuItemValue={(currency: string) => currency}
                     handleChange={handleCurrencySelection}
                     handleFilter={(currencyItems, filterValue) =>
                        currencyItems.filter(
                           (currency: string) =>
                              currency.toLowerCase().includes(filterValue.toLowerCase())
                        )
                     }
                  />
                  {getErrorComponent('currency')}
               </Form.Group>

               <AddInvoiceManuallyCompany
                  formData={formData}
                  setFormData={setFormData}
                  getErrorComponent={getErrorComponent}
                  isInvalid={isInvalid}
               />

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.reference')}</Form.Label>
                  <Form.Control
                     type="text"
                     name="reference"
                     placeholder={t('invoice:invoice.reference') ?? ''}
                     onChange={handleChange}
                  />
               </Form.Group>

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.paymentDate')}</Form.Label>
                  <DatePicker
                     selected={parsePaymentDate(formData.paymentDate)}
                     dateFormat='dd-MM-yyyy'
                     onChange={handlePaymentDateChange}
                     onKeyDown={(event) => event.preventDefault()}
                     className={isInvalid('date') ? 'is-invalid' : ''}
                     popperPlacement='top-end'
                     popperModifiers={[{ name: 'flip', enabled: false }]}
                     popperClassName='payment-date-popper add-invoice-date-popper'
                     portalId='add-invoice-payment-date-calendar-portal'
                     wrapperClassName='add-invoice-date-picker'
                     customInput={<PaymentDateButton />}
                     showPopperArrow={false}
                  />
                  {getErrorComponent('date')}
               </Form.Group>

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.vatCode')}</Form.Label>
                  <DropdownWithFilter
                     className={`toggle ${formData.vatCode ? '' : 'empty-value'}`}
                     items={vatCodes || []}
                     selectedValue={formData.vatCode}
                     getMenuItemValue={(vat: VatCodeDto) => vat?.code}
                     handleChange={handleVatChange}
                     handleFilter={(vatCodes, filterValue) =>
                        vatCodes.filter(
                           (vatCode: VatCodeDto) =>
                              vatCode.code.toLowerCase().includes(filterValue.toLowerCase())
                        )
                     }
                  />
               </Form.Group>

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.accountingNumber')}</Form.Label>
                  <DropdownWithFilter
                     className={`toggle ${isEmpty(formData.accountingNumber) ? '' : 'empty-value'}`}
                     items={accountingNumbers || []}
                     selectedValue={formData.accountingNumber.accountingNumber}
                     getMenuItemValue={(accountingNumber: AccountingNumberDto) =>
                        `${accountingNumber.accountingNumber} - ${accountingNumber.name}`
                     }
                     handleChange={handleAccountNumberSelection}
                     handleFilter={(accountNumbers, filterValue) =>
                        accountNumbers.filter(
                           (accountNumber: AccountingNumberDto) =>
                              accountNumber.accountingNumber
                                 .toLowerCase()
                                 .includes(filterValue.toLowerCase()) ||
                              accountNumber.name.toLowerCase().includes(filterValue.toLowerCase()),
                        )
                     }
                  />
               </Form.Group>

               <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('invoice:invoice.description')}</Form.Label>
                  <Form.Control
                     as="textarea"
                     name="description"
                     rows={3}
                     placeholder={'...'}
                     onChange={handleChange}
                  />
               </Form.Group>

               <Form.Group className="mb-2">
                  <UploadReceipt
                     file={file}
                     setFile={setFile}
                  />
               </Form.Group>
            </Form>
         </Modal.Body>

         <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleOnClose}>
               {t('common:common.cancel')}
            </Button>
            <Button className="primary" onClick={handleSave}>
               {t('common:common.save')}
            </Button>
         </Modal.Footer>
      </Modal>
   );
};

export default AddInvoiceManually;
